import React, { Children, cloneElement, isValidElement } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Close from '../icons/internals/Close';
import Error from '../icons/internals/Error';
import CheckCircle from '../icons/internals/CheckCircle';
import Rolling from '../icons/internals/Rolling';

const defaultPrefixCls = 'ne-input';

class Input extends React.Component {
  static propTypes = {
    /**
     * 多行，即textarea
     */
    multiline: PropTypes.bool,
    status: PropTypes.oneOf(['error', 'success', 'loading', 'default']),
    /**
     * 错误状态(todo)
     */
    error: PropTypes.bool,
    /**
     * 是否禁用
     */
    disabled: PropTypes.bool,
    /**
     * @ignore
     */
    prefixCls: PropTypes.string,
    /**
     * 声明 input 类型，同原生 input 标签的 type 属性
     */
    type: PropTypes.string,
    /**
     * 默认值
     */
    defaultValue: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    ]),
    /**
     * 是否显示清空按钮
     */
    clearable: PropTypes.bool,
    /**
     * 获取input ref
     */
    inputRef: PropTypes.func,
    /**
     * 清空输入框回调
     */
    onClear: PropTypes.func,
    /**
     * 清空按钮样式
     */
    clearIcon: PropTypes.node,
    /**
     * input元素的值，受控模式下使用
     */
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    ]),
    /**
     * @ignore
     */
    mask: PropTypes.node,
    /**
     * 前缀
     */
    prefix: PropTypes.node,
    /**
     * 后缀
     */
    suffix: PropTypes.node,
    /**
     * 是否只读
     */
    readOnly: PropTypes.bool,
    /**
     * 组件尺寸
     */
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    /**
     * 占位文本
     */
    placeholder: PropTypes.node,
    /**
     * textarea行数
     */
    rows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * 是否允许浏览器自动填充
     */
    autoComplete: PropTypes.oneOf(['off', 'on']),
    /**
     * 是否占满整行
     */
    fullWidth: PropTypes.bool,
  };

  static defaultProps = {
    defaultValue: '',
    readOnly: false,
    multiline: false,
    prefixCls: defaultPrefixCls,
    error: false,
    disabled: false,
    clearable: false,
    type: 'text',
    clearIcon: <Close />,
    size: 'medium',
    status: 'default',
    fullWidth: false,
    errorIcon: <Error className={`${defaultPrefixCls}-status-icon ne-icon-error`} />,
    loadingIcon: <Rolling className={`${defaultPrefixCls}-status-icon ne-icon-loading`} />,
    successIcon: <CheckCircle className={`${defaultPrefixCls}-status-icon ne-icon-success`} />,
  };

  static getDerivedStateFromProps(props, state) {
    const { defaultValue } = props;
    if (!state.isControlled && defaultValue !== state.prevDefaultValue) {
      return {
        value: defaultValue,
        prevDefaultValue: defaultValue,
      };
    }
    return null;
  }

  constructor(props, context) {
    super(props, context);
    const { value, defaultValue } = props;
    this.isControlled = value != null;
    this.state = {
      focused: false,
    };
    this.state = {
      value: defaultValue,
      prevDefaultValue: defaultValue,
      isControlled: this.isControlled,
      focused: false,
    };
  }

  componentDidUpdate() {
    const { value } = this.props;
    if (!this.isControlled && value != null) {
      this.isControlled = true;
      this.setState({
        value: null,
      });
    }
  }

  isControlled = false;

  handleChange = e => {
    const { onChange } = this.props;
    if (!this.isControlled) {
      this.setState({ value: e.target.value });
    }
    if (onChange) {
      onChange(e, e.target.value);
    }
  };

  handleFocus = e => {
    const { onFocus, disabled } = this.props;
    if (disabled) {
      e && e.stopPropagation();
      return;
    }

    this.setState({ focused: true });
    onFocus && onFocus(e);
  };

  handleBlur = e => {
    const { onBlur } = this.props;
    this.setState({ focused: false });
    onBlur && onBlur(e);
  };

  handleDeleteAll = e => {
    const { onDeleteAll, onClear, onChange } = this.props;
    if (!this.isControlled) {
      if (this.inputEl) this.inputEl.focus();
      this.setState({
        value: '',
      });
    }
    if (onClear) {
      onClear(e);
    } else if (onDeleteAll) {
      onDeleteAll(e);
    }
    if (this.inputEl && onChange) {
      this.inputEl.value = '';
      onChange({ target: this.inputEl, type: 'change' }, '');
    }
  };

  getInputEl = el => {
    this.inputEl = el;
    const { inputRef } = this.props;
    inputRef && inputRef(el);
  };

  handleClick = (...args) => {
    const { onClick } = this.props;
    this.inputEl && this.inputEl.focus();
    this.handleFocus();
    onClick && onClick(...args);
  };

  maskRender = () => {
    const { maskRender, disabled, prefixCls, mask, placeholder, id } = this.props;
    const { focused } = this.state;
    if (maskRender) {
      const handlers = {
        onChange: this.handleChange,
        onFocus: this.handleFocus,
        onBlur: this.handleBlur,
      };
      return maskRender(this.props, handlers, focused);
    }
    return (
      <div
        // tabIndex={disabled ? '-1' : isValidElement(mask) ? null : '0'}
        className={classNames(`${prefixCls}-mask`, {
          [`${prefixCls}-mask-placeholder`]: mask.length === 0,
        })}>
        <input
          onChange={this.handleChange}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          ref={this.getInputEl}
          aria-invalid
          className={`${prefixCls}-mask-input`}
          id={id}
          disabled={disabled}/>
        <div className={`${prefixCls}-mask-content`}>
          {!mask || mask.length <= 0 ? placeholder : mask}
        </div>
      </div>
    );
  };

  render() {
    const {
      error,
      type,
      className,
      prefixCls,
      showDeleteBtn,
      clearable,
      prefix,
      clearIcon,
      onDeleteAll,
      value,
      defaultValue,
      inputRef,
      inputProps,
      disabled,
      suffix,
      mask,
      multiline,
      onClick,
      size,
      readOnly,
      placeholder,
      rows,
      rowsMax,
      onChange,
      autoComplete,
      onBlur,
      onFocus,
      status,
      fullWidth,
      errorIcon,
      loadingIcon,
      successIcon,
      showStatusIcon,
      onClear,
      maskRender,
      style,
      ...other
    } = this.props;
    const { focused, value: stateValue } = this.state;
    const classes = classNames(
      `${prefixCls}-container`,
      {
        [`${prefixCls}-focused`]: focused,
        [`${prefixCls}-fullWidth`]: fullWidth,
        [`${prefixCls}-multiline`]: multiline,
        [`${prefixCls}-error`]: error,
        [`${prefixCls}-${status}`]: status,
        [`${prefixCls}-disabled`]: disabled,
        [`${prefixCls}-${size}`]: size,
      },
      className,
    );
    const inputValue = this.isControlled ? value : stateValue;
    const deleteAllBtn = cloneElement(clearIcon, {
      className: classNames(
        `${prefixCls}-delete`,
        {
          [`${prefixCls}-delete-show`]: inputValue,
        },
        clearIcon.props.className,
      ),
      onClick: this.handleDeleteAll,
    });
    const InputBase = multiline ? 'textarea' : 'input';
    let newPrefix = Children.map(prefix, item => {
      if (isValidElement(item)) {
        return cloneElement(item, {
          className: classNames(item.props.className, `${prefixCls}-prefix`),
        });
      }
    });
    if (newPrefix && newPrefix.length < 1) {
      newPrefix = <span className={`${prefixCls}-prefix`}>{prefix}</span>;
    }
    let newSuffix = Children.map(suffix, item => {
      if (isValidElement(item)) {
        return cloneElement(item, {
          className: classNames(item.props.className, `${prefixCls}-suffix`),
        });
      }
    });
    if (newSuffix && newSuffix.length < 1) {
      newSuffix = <span className={`${prefixCls}-suffix`}>{suffix}</span>;
    }
    const statusIcons = {
      error: errorIcon,
      loading: loadingIcon,
      success: successIcon,
    };
    const statusIcon = isValidElement(statusIcons[status])
      ? cloneElement(statusIcons[status], {
          className: classNames(statusIcons[status].props.className, `${prefixCls}-status-icon`),
        })
      : null;

    return (
      <div className={classes} onClick={this.handleClick} style={style}>
        {newPrefix}
        {mask != null || maskRender ? (
          this.maskRender()
        ) : (
          <InputBase
            autoComplete={autoComplete}
            disabled={disabled}
            ref={this.getInputEl}
            className={classNames(prefixCls)}
            type={type}
            value={inputValue}
            readOnly={!!(mask || maskRender || readOnly)}
            placeholder={placeholder}
            rows={rows}
            aria-invalid={error || status === 'error'}
            {...other}
            onChange={this.handleChange}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}/>
        )}
        {clearable || showDeleteBtn ? deleteAllBtn : null}
        {showStatusIcon && statusIcon ? statusIcon : newSuffix}
      </div>
    );
  }
}

export default Input;



// WEBPACK FOOTER //
// ./src/input/Input.jsx